import { cn } from '@/lib/cn';

/**
 * The standard page header: title, optional description, optional actions.
 *
 * Every screen uses this rather than hand-rolling a heading row, so the vertical rhythm
 * and the position of the primary action are identical everywhere — the thing users
 * learn once and then rely on without noticing.
 *
 * The h1 carries tabIndex={-1} so the shell can move focus here on navigation without
 * putting the heading in the tab order.
 */
export function PageHeader({
  title,
  description,
  actions,
  className,
}: {
  // ReactNode, not string: the four pages that need a badge or an icon beside the
  // heading currently hand-roll their own <h1>, and each copy has to remember the
  // tabIndex={-1} that AppShell's route-change focus depends on.
  title: React.ReactNode;
  description?: string;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between',
        className,
      )}
    >
      <div className="min-w-0 space-y-1">
        <h1 tabIndex={-1} className="text-h1 outline-none">
          {title}
        </h1>
        {description && <p className="max-w-2xl text-body text-fg-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/**
 * A single headline figure.
 *
 * `derived` marks a calculated or estimated number so it is never mistaken for a
 * measurement — a hard requirement from docs/PROJECT_OVERVIEW.md, and the reason this
 * is a component rather than markup repeated per dashboard.
 */
export function StatTile({
  label,
  value,
  hint,
  trend,
  derived = false,
  icon,
  className,
}: {
  label: string;
  value: string | number;
  hint?: string;
  trend?: { direction: 'up' | 'down' | 'flat'; label: string; good?: boolean };
  derived?: boolean;
  icon?: React.ReactNode;
  className?: string;
}) {
  const trendTone =
    trend?.direction === 'flat'
      ? 'text-fg-muted'
      : trend?.good === false
        ? 'text-danger'
        : 'text-success';

  // Matches MetricTile: figure on the surface, provenance and trend in a tinted band
  // across the foot. The two components stay separate (four pages depend on this one's
  // props) but they must not look like two different products.
  const hasFooter = Boolean(trend || hint || derived);

  return (
    // h-full: a tile with no footer band sat shorter than its neighbour in the same
    // grid row, which is visible the moment two tiles are side by side on a phone.
    <div
      className={cn('flex h-full flex-col overflow-hidden rounded-lg bg-surface shadow-sm', className)}
    >
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-2">
          <p className="text-caption font-medium text-fg-muted">{label}</p>
          {icon && <span className="shrink-0 text-fg-subtle">{icon}</span>}
        </div>

        <p className="tabular mt-1 text-metric font-semibold tracking-tighter">{value}</p>
      </div>

      {hasFooter && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-line bg-subtle px-5 py-2.5">
          {trend && (
            <span className={cn('text-caption font-medium', trendTone)}>
              {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→'}{' '}
              {trend.label}
            </span>
          )}
          {hint && <span className="text-caption text-fg-subtle">{hint}</span>}
          {derived && (
            <span
              title="Calculated figure, not a direct measurement"
              className="rounded-full bg-surface px-2 py-0.5 text-caption text-fg-muted"
            >
              calculated
            </span>
          )}
        </div>
      )}
    </div>
  );
}
